import './SectionLabel.css';
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { StatusChip, type StatusChipProps } from './StatusChip';

/** Dica que troca "Clique" por "Toque" conforme o ponteiro (§3.9: `uf.hint.mouse` / `uf.hint.touch`). */
export interface SectionLabelHint {
  mouse: string;
  touch: string;
}

export interface SectionLabelProps {
  /** Rótulo da seção, string pronta do dono ("Presidente em São Paulo", "Governador"). */
  label: string;
  /** Nível do título; padrão `h3` (§3.9, navegação por títulos). */
  level?: 2 | 3 | 4;
  /** id do título, para `aria-labelledby` da seção do dono. */
  id?: string;
  /** Situação à direita (props do `StatusChip`). Prevalece sobre `hint`. */
  status?: StatusChipProps;
  /** Dica à direita: texto pronto ou o par mouse/toque, escolhido por `matchMedia('(pointer: coarse)')`, reativo. */
  hint?: string | SectionLabelHint;
  /** Ações à direita (ex.: `Pager` de §8.4, §8.5 e §11.1). Prevalecem sobre `status` e `hint`. */
  actions?: ComponentChildren;
}

const COARSE = '(pointer: coarse)';

function coarseNow(): boolean {
  return typeof matchMedia === 'function' && matchMedia(COARSE).matches;
}

/** `pointer: coarse` agora e a cada mudança (tablet com teclado acoplado, emulação), só quando a dica depende dele. */
function useCoarsePointer(enabled: boolean): boolean {
  const [coarse, setCoarse] = useState(() => enabled && coarseNow());
  useEffect(() => {
    if (!enabled || typeof matchMedia !== 'function') return;
    const mq = matchMedia(COARSE);
    const sync = () => setCoarse(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [enabled]);
  return enabled && coarse;
}

/** Rótulo de seção de cartão (§3.9 `SectionLabel`): título `h3` à esquerda; `StatusChip`, dica ou ações à direita. */
export function SectionLabel({ label, level = 3, id, status, hint, actions }: SectionLabelProps) {
  const Heading = `h${level}` as 'h2' | 'h3' | 'h4';
  const paired = typeof hint === 'object' && hint !== null;
  const coarse = useCoarsePointer(paired);
  const hintText = paired ? (coarse ? hint.touch : hint.mouse) : hint;
  const hasActions = actions !== undefined && actions !== null && actions !== false;

  let side: ComponentChildren = null;
  if (hasActions) side = <div class="section-label__actions">{actions}</div>;
  else if (status) side = <StatusChip {...status} />;
  else if (hintText)
    side = (
      <p class="section-label__hint" data-pointer={paired ? (coarse ? 'coarse' : 'fine') : undefined}>
        {hintText}
      </p>
    );

  return (
    <div class="section-label" data-testid="section-label">
      <Heading class="section-label__title" id={id}>
        {label}
      </Heading>
      {side}
    </div>
  );
}
